import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router'
import { parentApi, ApiError, type ParentChild } from '@/lib/parent-api'
import { invitationsApi, type PendingInvitation } from '@/lib/invitations-api'
import { useAuthStore } from '@/stores/auth.store'
import { PendingInvitations } from '@/components/invitations'
import { TimelineView } from '@/components/timeline'
import { PullToRefresh } from '@/components/common/PullToRefresh'
import { trackEvent, Events } from '@/lib/observability'
import { UPDATE_TYPES, UPDATE_TYPE_LABELS, type Update, type UpdateType } from '@/types/update'

export function ParentTimelinePage() {
  const user = useAuthStore((state) => state.user)
  const isEmailVerified = user?.emailVerified ?? false

  const [children, setChildren] = useState<ParentChild[]>([])
  const [updates, setUpdates] = useState<Update[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)

  const [filterChildId, setFilterChildId] = useState<string | 'all'>('all')
  const [filterType, setFilterType] = useState<UpdateType | 'all'>('all')

  useEffect(() => {
    loadData()
  }, [isEmailVerified])

  useEffect(() => {
    if (children.length > 0) {
      loadUpdates()
    }
  }, [filterChildId, filterType, children.length])

  const loadData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Always load children and invitations
      const [childrenRes, invitationsRes] = await Promise.all([
        parentApi.getChildren(),
        invitationsApi.getPendingInvitations(),
      ])
      setChildren(childrenRes.children)
      setPendingInvitations(invitationsRes.invitations)

      // Only load updates if email is verified
      if (isEmailVerified) {
        try {
          const updatesRes = await parentApi.getUpdates()
          setUpdates(updatesRes.updates)
          setHasMore(updatesRes.hasMore)
          setNextCursor(updatesRes.nextCursor)
        } catch (err) {
          // If it's an email verification error, don't show as error - we handle it with the banner
          if (err instanceof ApiError && err.message.includes('verification')) {
            setUpdates([])
            setHasMore(false)
            setNextCursor(null)
          } else {
            throw err
          }
        }
      } else {
        setUpdates([])
        setHasMore(false)
        setNextCursor(null)
      }

      // Track timeline view
      trackEvent(Events.TIMELINE_VIEWED, {
        child_count: childrenRes.children.length,
        pending_invitations: invitationsRes.invitations.length,
      })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadUpdates = async () => {
    // Don't try to load updates if not verified
    if (!isEmailVerified) {
      setUpdates([])
      setHasMore(false)
      setNextCursor(null)
      return
    }

    try {
      const params: { childId?: string; type?: string } = {}
      if (filterChildId !== 'all') params.childId = filterChildId
      if (filterType !== 'all') params.type = filterType

      const response = await parentApi.getUpdates(params)
      setUpdates(response.updates)
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch (err) {
      // Ignore verification errors, handle with banner
      if (err instanceof ApiError && err.message.includes('verification')) {
        setUpdates([])
        setHasMore(false)
        setNextCursor(null)
      } else {
        setError(err instanceof ApiError ? err.message : 'Failed to load updates')
      }
    }
  }

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore || !isEmailVerified) return

    try {
      setIsLoadingMore(true)
      const params: { childId?: string; type?: string; before: string } = { before: nextCursor }
      if (filterChildId !== 'all') params.childId = filterChildId
      if (filterType !== 'all') params.type = filterType

      const response = await parentApi.getUpdates(params)
      setUpdates((prev) => [...prev, ...response.updates])
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load more updates')
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleInvitationAccepted = async (invitation: PendingInvitation) => {
    // Remove from pending list
    setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id))
    // Reload children and updates to include the newly accessible child
    const [childrenRes, updatesRes] = await Promise.all([
      parentApi.getChildren(),
      parentApi.getUpdates(),
    ])
    setChildren(childrenRes.children)
    setUpdates(updatesRes.updates)
    setHasMore(updatesRes.hasMore)
    setNextCursor(updatesRes.nextCursor)
  }

  const handleInvitationDeclined = (invitation: PendingInvitation) => {
    setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitation.id))
  }

  const handleRefresh = useCallback(async () => {
    setError(null)
    const [childrenRes, invitationsRes] = await Promise.all([
      parentApi.getChildren(),
      invitationsApi.getPendingInvitations(),
    ])
    setChildren(childrenRes.children)
    setPendingInvitations(invitationsRes.invitations)

    if (isEmailVerified) {
      const params: { childId?: string; type?: string } = {}
      if (filterChildId !== 'all') params.childId = filterChildId
      if (filterType !== 'all') params.type = filterType
      const response = await parentApi.getUpdates(params)
      setUpdates(response.updates)
      setHasMore(response.hasMore)
      setNextCursor(response.nextCursor)
    }
  }, [isEmailVerified, filterChildId, filterType])

  if (isLoading) {
    return <div className="p-4">Loading...</div>
  }

  const hasNoContent = children.length === 0 && pendingInvitations.length === 0

  if (hasNoContent) {
    return (
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-4">
          <h1 className="mb-4 text-2xl font-bold">Timeline</h1>
          <div className="rounded border border-dashed border-gray-300 p-8 text-center">
            <p className="text-gray-600">No children linked to your account yet.</p>
            <p className="mt-2 text-sm text-gray-500">
              Your childminder will send you an invitation to link your accounts.
            </p>
          </div>
        </div>
      </PullToRefresh>
    )
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">Timeline</h1>

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Pending Invitations */}
      <PendingInvitations
        invitations={pendingInvitations}
        onAccepted={handleInvitationAccepted}
        onDeclined={handleInvitationDeclined}
      />

      {/* Children List */}
      {children.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-lg font-medium">Your Children</h2>
          <div className="flex flex-wrap gap-2">
            {children.map((child) => (
              <Link
                key={child.id}
                to={`/my-children/${child.id}`}
                className="rounded-lg border border-gray-200 p-3 hover:border-primary-300 hover:bg-primary-50"
              >
                <p className="font-medium ph-no-capture">{child.firstName} {child.lastName}</p>
                <p className="text-xs text-gray-500 capitalize">{child.relationship}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Only show filters and updates if there are children */}
      {children.length > 0 && (
        <>
          {/* Filters */}
          <section className="mb-4">
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                onClick={() => setFilterChildId('all')}
                className={`rounded-full px-3 py-1 text-sm ${
                  filterChildId === 'all'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Children
              </button>
              {children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => setFilterChildId(child.id)}
                  className={`rounded-full px-3 py-1 text-sm ph-no-capture ${
                    filterChildId === child.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {child.firstName}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterType('all')}
                className={`rounded-full px-3 py-1 text-sm ${
                  filterType === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Types
              </button>
              {UPDATE_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={`rounded-full px-3 py-1 text-sm ${
                    filterType === t
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {UPDATE_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </section>

          {/* Timeline */}
          <section>
            <h2 className="mb-4 text-lg font-medium">Timeline</h2>
            {!isEmailVerified ? (
              <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-6 text-center text-amber-700">
                <p className="font-medium">Verify your email to view updates</p>
                <p className="mt-1 text-sm text-amber-600">Check your inbox for the verification link</p>
              </div>
            ) : updates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
                <p className="font-medium">No updates yet</p>
                <p className="mt-1 text-sm">Updates from your childminder will appear here</p>
              </div>
            ) : (
              <TimelineView
                updates={updates}
                showChildren={filterChildId === 'all' && children.length > 1}
                hasMore={hasMore}
                isLoadingMore={isLoadingMore}
                onLoadMore={loadMore}
              />
            )}
          </section>
        </>
      )}
      </div>
    </PullToRefresh>
  )
}
